/**
 * ImpactWarningDialog - Warns users when changes will affect dependent views
 *
 * Shows a list of affected views and requires explicit confirmation
 * before proceeding with potentially breaking changes.
 */

import React from 'react';
import { AlertTriangle, Database } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import type { AffectedView } from '@/types/views';

export interface ImpactWarningDialogProps {
  isOpen: boolean;
  affectedViews: AffectedView[];
  changeType: 'edit' | 'delete';
  viewName?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * ImpactWarningDialog Component
 *
 * Shows warning when changes would affect other views.
 * Lists affected views and requires confirmation.
 */
export const ImpactWarningDialog: React.FC<ImpactWarningDialogProps> = ({
  isOpen,
  affectedViews,
  changeType,
  viewName,
  onConfirm,
  onCancel,
}) => {
  const directViews = affectedViews.filter((v) => v.impact_type === 'direct');
  const transitiveViews = affectedViews.filter((v) => v.impact_type === 'transitive');

  const getTitle = () => {
    if (changeType === 'delete') {
      return 'Confirm View Deletion';
    }
    return 'Confirm Changes to View';
  };

  const getDescription = () => {
    const count = affectedViews.length;
    const viewText = count === 1 ? 'view depends' : 'views depend';

    if (changeType === 'delete') {
      return `Deleting "${viewName}" will break ${count} other ${viewText} on it. These views will fail until they are updated or deleted.`;
    }
    return `Changes to "${viewName}" may affect ${count} other ${viewText} on it. These views may need to be updated to reflect your changes.`;
  };

  const getActionText = () => {
    if (changeType === 'delete') {
      return 'Delete Anyway';
    }
    return 'Save Changes';
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={(open) => !open && onCancel()}>
      <AlertDialogContent className="max-w-2xl">
        <AlertDialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
              <AlertTriangle className="h-6 w-6 text-destructive" />
            </div>
            <div>
              <AlertDialogTitle>{getTitle()}</AlertDialogTitle>
              <AlertDialogDescription className="mt-1">
                {getDescription()}
              </AlertDialogDescription>
            </div>
          </div>
        </AlertDialogHeader>

        <div className="my-4">
          <h4 className="text-sm font-semibold mb-3">Affected Views:</h4>
          <ScrollArea className="h-[300px] rounded-md border">
            <div className="p-4 space-y-2">
              {/* Direct dependents */}
              {directViews.length > 0 && (
                <>
                  {directViews.map((view) => (
                    <AffectedViewItem key={view.view_id} view={view} />
                  ))}
                </>
              )}

              {/* Transitive dependents (if any) */}
              {transitiveViews.length > 0 && (
                <>
                  {directViews.length > 0 && (
                    <div className="my-4 border-t pt-4">
                      <p className="text-xs text-muted-foreground mb-2">
                        Indirectly affected (through other views):
                      </p>
                    </div>
                  )}
                  {transitiveViews.map((view) => (
                    <AffectedViewItem key={view.view_id} view={view} />
                  ))}
                </>
              )}
            </div>
          </ScrollArea>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            className={
              changeType === 'delete'
                ? 'bg-destructive hover:bg-destructive/90 text-destructive-foreground'
                : ''
            }
          >
            {getActionText()}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

/**
 * AffectedViewItem - Renders a single affected view item
 */
interface AffectedViewItemProps {
  view: AffectedView;
}

const AffectedViewItem: React.FC<AffectedViewItemProps> = ({ view }) => {
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg border bg-card">
      <Database className="h-4 w-4 text-muted-foreground flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{view.view_name}</p>
        <p className="text-xs text-muted-foreground font-mono truncate">{view.view_id}</p>
      </div>
      <Badge
        variant={view.impact_type === 'direct' ? 'destructive' : 'secondary'}
        className="text-xs flex-shrink-0"
      >
        {view.impact_type === 'direct' ? 'Direct' : 'Indirect'}
      </Badge>
    </div>
  );
};
